/**
 * onboardingTransforms.ts
 * Transform onboarding draft data into persistent schema entities
 *
 * Responsibilities:
 * - Convert temporary UUIDs to real UUIDs
 * - Add timestamps, status fields, and metadata
 * - Map relationships between entities
 * - Maintain ID mappings for relationship resolution
 */

import { generateUUID } from '@/utils/uuid';
import { toMoneyInteger, getPrecisionForCurrency } from '@/utils/moneyHelpers';
import type { OnboardingDraft } from '@/types/transient/OnboardingDraft';
import type { Strategy } from '@/types/schema/Strategy';
import type { Playbook } from '@/types/schema/Playbook';
import type { MarketConditionCategory } from '@/types/schema/MarketConditionCategory';
import type { EntryModelCategory } from '@/types/schema/EntryModelCategory';
import type { PlaybookCriteriaItem } from '@/types/schema/PlaybookCriteriaItem';
import type { Confluence } from '@/types/schema/Confluence';
import type { EntryModel } from '@/types/schema/EntryModel';
import type { Broker, BrokerFeeConfiguration } from '@/types/schema/Broker';
import type { Account } from '@/types/schema/Account';
import type { BalanceHistory } from '@/types/schema/BalanceHistory';
import type { AccountRisk, RiskProfile } from '@/types/schema/AccountRisk';
import type { BasicSettings } from '@/types/schema/BasicSettings';
import { BROKER_CATALOG_DATA } from '@/data/brokerCatalog';
import { BROKER_FEE_CATALOG_DATA } from '@/data/brokerFeeCatalog';
import type { BrokerFeeConfiguration as RefBrokerFeeConfiguration } from '@/types/reference/BrokerFeeCatalog';

/**
 * Result of a transformation operation
 */
export interface TransformResult<T> {
  /** Transformed entities */
  entities: T[];
  /** Map from temp IDs to real IDs */
  idMap: Map<string, string>;
}

/**
 * Helper function to generate timestamps
 */
const now = (): string => new Date().toISOString();

/**
 * Transform market condition categories from draft to schema
 */
export function transformMarketConditionCategories(
  draft: OnboardingDraft
): TransformResult<MarketConditionCategory> {
  const categories = draft.trading?.marketConditionCategories || [];
  const idMap = new Map<string, string>();

  const entities = categories.map((category, index) => {
    const realId = generateUUID();
    idMap.set(category.tempId, realId);

    const entity: MarketConditionCategory = {
      id: realId,
      createdAt: now(),
      updatedAt: now(),
      name: category.name,
      color: category.color,
      order: category.order ?? index,
      status: 'active' as const,
    };

    if (category.description) {
      entity.description = category.description;
    }

    return entity;
  });

  return { entities, idMap };
}

/**
 * Transform entry model categories from draft to schema
 */
export function transformEntryModelCategories(
  draft: OnboardingDraft
): TransformResult<EntryModelCategory> {
  const categories = draft.trading?.entryModelCategories || [];
  const idMap = new Map<string, string>();

  const entities = categories.map((category, index) => {
    const realId = generateUUID();
    idMap.set(category.tempId, realId);

    const entity: EntryModelCategory = {
      id: realId,
      createdAt: now(),
      updatedAt: now(),
      name: category.name,
      color: category.color,
      order: category.order ?? index,
      status: 'active' as const,
    };

    if (category.description) {
      entity.description = category.description;
    }

    return entity;
  });

  return { entities, idMap };
}

/**
 * Transform criteria items from draft to schema
 */
export function transformCriteriaItems(
  draft: OnboardingDraft,
  marketConditionIdMap: Map<string, string>
): TransformResult<PlaybookCriteriaItem> {
  const items = draft.trading?.criteriaItems || [];
  const idMap = new Map<string, string>();

  const entities = items.map(item => {
    const realId = generateUUID();
    idMap.set(item.tempId, realId);

    const entity: PlaybookCriteriaItem = {
      id: realId,
      createdAt: now(),
      updatedAt: now(),
      name: item.name,
      status: 'active' as const,
    };

    if (item.description) {
      entity.description = item.description;
    }

    if (item.categoryId) {
      const mappedId = marketConditionIdMap.get(item.categoryId);
      if (mappedId) {
        entity.marketConditionCategoryId = mappedId;
      }
    }

    return entity;
  });

  return { entities, idMap };
}

/**
 * Transform confluences from draft to schema
 */
export function transformConfluences(
  draft: OnboardingDraft,
  marketConditionIdMap: Map<string, string>
): TransformResult<Confluence> {
  const confluences = draft.trading?.confluences || [];
  const idMap = new Map<string, string>();

  const entities = confluences.map(confluence => {
    const realId = generateUUID();
    idMap.set(confluence.tempId, realId);

    const entity: Confluence = {
      id: realId,
      createdAt: now(),
      updatedAt: now(),
      name: confluence.name,
      status: 'active' as const,
    };

    if (confluence.description) {
      entity.description = confluence.description;
    }

    if (confluence.categoryId) {
      const mappedId = marketConditionIdMap.get(confluence.categoryId);
      if (mappedId) {
        entity.marketConditionCategoryId = mappedId;
      }
    }

    return entity;
  });

  return { entities, idMap };
}

/**
 * Transform entry models from draft to schema
 */
export function transformEntryModels(
  draft: OnboardingDraft,
  entryModelCategoryIdMap: Map<string, string>
): TransformResult<EntryModel> {
  const models = draft.trading?.entryModels || [];
  const idMap = new Map<string, string>();

  const entities = models.map(model => {
    const realId = generateUUID();
    idMap.set(model.tempId, realId);

    const entity: EntryModel = {
      id: realId,
      createdAt: now(),
      updatedAt: now(),
      name: model.name,
      status: 'active' as const,
    };

    if (model.description) {
      entity.description = model.description;
    }

    if (model.categoryId) {
      const mappedId = entryModelCategoryIdMap.get(model.categoryId);
      if (mappedId) {
        entity.entryModelCategoryId = mappedId;
      }
    }

    return entity;
  });

  return { entities, idMap };
}

/**
 * Transform playbooks from draft to schema
 */
export function transformPlaybooks(
  draft: OnboardingDraft,
  criteriaIdMap: Map<string, string>,
  confluenceIdMap: Map<string, string>,
  entryModelIdMap: Map<string, string>
): TransformResult<Playbook> {
  const playbooks = draft.trading?.playbooks || [];
  const idMap = new Map<string, string>();

  const entities = playbooks.map(playbook => {
    const realId = generateUUID();
    idMap.set(playbook.tempId, realId);

    const entity: Playbook = {
      id: realId,
      createdAt: now(),
      updatedAt: now(),
      name: playbook.name,
      status: 'active' as const,
      entryModelIds: playbook.entryModelIds.map(tempId =>
        entryModelIdMap.get(tempId) || tempId
      ),
    };

    if (playbook.description) {
      entity.description = playbook.description;
    }

    if (playbook.philosophy) {
      entity.philosophy = playbook.philosophy;
    }

    if (playbook.criteriaIds && playbook.criteriaIds.length > 0) {
      entity.criteriaIds = playbook.criteriaIds.map(tempId =>
        criteriaIdMap.get(tempId) || tempId
      );
    }

    // Build confluences array with user-selected polarity
    if (playbook.confluences && playbook.confluences.length > 0) {
      entity.confluences = playbook.confluences.map(link => ({
        confluenceId: confluenceIdMap.get(link.confluenceId) || link.confluenceId,
        polarity: link.polarity,
      }));
    }

    return entity;
  });

  return { entities, idMap };
}

/**
 * Transform strategy from draft to schema
 */
export function transformStrategy(
  draft: OnboardingDraft,
  playbookIdMap: Map<string, string>
): Strategy {
  const strategy = draft.trading?.strategy;

  if (!strategy) {
    throw new Error('Strategy is required for transformation');
  }

  const playbookIds = (draft.trading?.playbooks || [])
    .map(p => playbookIdMap.get(p.tempId))
    .filter((id): id is string => id !== undefined);

  const entity: Strategy = {
    id: generateUUID(),
    createdAt: now(),
    updatedAt: now(),
    name: strategy.name,
    status: 'active',
    playbookIds,
  };

  if (strategy.description) {
    entity.description = strategy.description;
  }

  if (strategy.philosophy) {
    entity.philosophy = strategy.philosophy;
  }

  // tags and order are optional organizational fields - not set during onboarding

  return entity;
}

/**
 * Transform all trading data in correct dependency order
 */
export function transformTradingData(draft: OnboardingDraft): {
  marketConditionCategories: MarketConditionCategory[];
  entryModelCategories: EntryModelCategory[];
  criteriaItems: PlaybookCriteriaItem[];
  confluences: Confluence[];
  entryModels: EntryModel[];
  playbooks: Playbook[];
  strategy: Strategy;
  strategyIdMap: Map<string, string>;
  playbookIdMap: Map<string, string>;
} {
  // Transform categories first (no dependencies)
  const { entities: marketConditionCategories, idMap: marketConditionIdMap } =
    transformMarketConditionCategories(draft);
  const { entities: entryModelCategories, idMap: entryModelCategoryIdMap } =
    transformEntryModelCategories(draft);

  // Transform items (depend on categories)
  const { entities: criteriaItems, idMap: criteriaIdMap } =
    transformCriteriaItems(draft, marketConditionIdMap);
  const { entities: confluences, idMap: confluenceIdMap } =
    transformConfluences(draft, marketConditionIdMap);
  const { entities: entryModels, idMap: entryModelIdMap } =
    transformEntryModels(draft, entryModelCategoryIdMap);

  // Transform playbooks (depend on items)
  const { entities: playbooks, idMap: playbookIdMap } =
    transformPlaybooks(draft, criteriaIdMap, confluenceIdMap, entryModelIdMap);

  // Transform strategy (depends on playbooks)
  const strategy = transformStrategy(draft, playbookIdMap);

  // Create strategy ID map (for risk configuration)
  const strategyIdMap = new Map<string, string>();
  if (draft.trading?.strategy) {
    // In onboarding there's only one strategy, using a stable temp ID
    strategyIdMap.set('strategy-temp-id', strategy.id);
  }

  return {
    marketConditionCategories,
    entryModelCategories,
    criteriaItems,
    confluences,
    entryModels,
    playbooks,
    strategy,
    strategyIdMap,
    playbookIdMap
  };
}

/**
 * Transform broker from draft to schema
 */
export function transformBroker(draft: OnboardingDraft): Broker {
  const account = draft.account;

  if (!account?.selectedBrokerId) {
    throw new Error('Broker ID is required for transformation');
  }

  const brokerId = account.selectedBrokerId;
  const feeConfig = account.feeConfiguration;

  // Get broker metadata from catalog
  const brokerCatalogEntry = BROKER_CATALOG_DATA.find(b => b.id === brokerId);

  // Determine broker name and reference ID
  let brokerName: string;
  let brokerReferenceId: string | undefined;

  if (brokerId === 'custom-broker') {
    brokerName = account.brokerSnapshot?.brokerName || 'Custom Broker';
    brokerReferenceId = undefined; // Custom brokers have no reference
  } else {
    brokerName = brokerCatalogEntry?.name || brokerId;
    brokerReferenceId = brokerId;
  }

  // Transform fee configurations
  const feeConfigurations: BrokerFeeConfiguration[] = [];

  if (feeConfig?.choice === 'custom' && feeConfig.customFeeConfigs) {
    // User created custom fee configurations
    feeConfigurations.push(...feeConfig.customFeeConfigs.map(refConfig => {
      const schemaConfig: BrokerFeeConfiguration = {
        id: refConfig.id,
        name: refConfig.name,
        status: 'active',
        commission: refConfig.commission,
        feesUpdatedAt: now()
      };

      // Optional fields
      if (refConfig.category) schemaConfig.category = refConfig.category;
      if (refConfig.instrumentId) schemaConfig.instrumentId = refConfig.instrumentId;
      if (refConfig.appliesTo) schemaConfig.appliesTo = refConfig.appliesTo;
      if (refConfig.notes) schemaConfig.notes = refConfig.notes;

      // Fee structures (cast from reference to schema types - compatible at runtime)
      if (refConfig.overnightFinancing) {
        schemaConfig.overnightFinancing = refConfig.overnightFinancing as unknown as NonNullable<typeof schemaConfig.overnightFinancing>;
      }
      if (refConfig.currencyConversion) {
        schemaConfig.currencyConversion = refConfig.currencyConversion as unknown as NonNullable<typeof schemaConfig.currencyConversion>;
      }

      return schemaConfig;
    }));
  } else if (feeConfig?.choice === 'standard' && brokerReferenceId) {
    // User accepted standard catalog fees - load from broker fee catalog
    const feeData = BROKER_FEE_CATALOG_DATA.get(brokerReferenceId);

    if (feeData?.feeConfigurations) {
      feeConfigurations.push(...feeData.feeConfigurations.map((refConfig: RefBrokerFeeConfiguration) => {
        const schemaConfig: BrokerFeeConfiguration = {
          id: refConfig.id,
          name: refConfig.name,
          status: 'active',
          commission: refConfig.commission,
          feesUpdatedAt: now()
        };

        // Optional fields
        if (refConfig.category) schemaConfig.category = refConfig.category;
        if (refConfig.instrumentId) schemaConfig.instrumentId = refConfig.instrumentId;
        if (refConfig.appliesTo) schemaConfig.appliesTo = refConfig.appliesTo;
        if (refConfig.notes) schemaConfig.notes = refConfig.notes;

        // Fee structures
        if (refConfig.overnightFinancing) {
          schemaConfig.overnightFinancing = refConfig.overnightFinancing as unknown as NonNullable<typeof schemaConfig.overnightFinancing>;
        }
        if (refConfig.currencyConversion) {
          schemaConfig.currencyConversion = refConfig.currencyConversion as unknown as NonNullable<typeof schemaConfig.currencyConversion>;
        }

        return schemaConfig;
      }));
    }
  }

  // Create broker entity
  const entity: Broker = {
    id: generateUUID(),
    createdAt: now(),
    updatedAt: now(),
    name: brokerName,
    status: 'active',
    feeConfigurations,
  };

  // Optional fields
  if (account.brokerSnapshot?.context) {
    entity.description = account.brokerSnapshot.context;
  }

  if (brokerReferenceId) {
    entity.brokerReferenceId = brokerReferenceId;
    entity.brokerReferenceUpdatedAt = now();
  }

  // Load fee metadata from catalog if available
  if (brokerReferenceId) {
    const feeData = BROKER_FEE_CATALOG_DATA.get(brokerReferenceId);
    if (feeData?.feeMetadata) {
      entity.feeMetadata = feeData.feeMetadata;
    }
  }

  return entity;
}

/**
 * Transform account from draft to schema
 *
 * @param draft - Onboarding draft data
 * @param broker - Transformed broker entity (needed to check fee configurations)
 */
export function transformAccount(
  draft: OnboardingDraft,
  broker: Broker
): Account {
  const account = draft.account;

  if (!account?.accountName || !account?.accountType) {
    throw new Error('Account name and type are required for transformation');
  }

  // Derive fee preferences from broker's fee configurations
  const hasOvernightFinancing = broker.feeConfigurations.some(
    config => config.overnightFinancing !== undefined
  );
  const hasCurrencyConversion = broker.feeConfigurations.some(
    config => config.currencyConversion !== undefined
  );

  // Create account entity
  const entity: Account = {
    id: generateUUID(),
    createdAt: now(),
    updatedAt: now(),
    name: account.accountName,
    accountType: account.accountType,
    status: 'active',
    brokerId: broker.id,
  };

  // Optional core fields
  if (account.accountDescription) {
    entity.description = account.accountDescription;
  }

  if (account.customAccountTypeName) {
    entity.customAccountTypeName = account.customAccountTypeName;
  }

  if (account.accountNumber) {
    entity.accountNumber = account.accountNumber;
  }

  // Base currency (optional, defaults to broker default or USD)
  if (account.baseCurrency) {
    entity.baseCurrency = account.baseCurrency;
  } else if (broker.feeMetadata?.defaultAccountCurrency) {
    entity.baseCurrency = broker.feeMetadata.defaultAccountCurrency;
  }

  // Fee preferences (optional object - only set if there are fee configurations)
  if (broker.feeConfigurations.length > 0) {
    entity.feePreferences = {
      includeFeeInPnL: true,
      promptFeeVerification: true,
      trackOvernightFinancing: hasOvernightFinancing,
      trackCurrencyConversion: hasCurrencyConversion
    };
  }

  // Prop firm details (only for prop accounts)
  if (account.propFirmDetails && account.accountType === 'prop') {
    type PropFirmName = NonNullable<Account['propFirmDetails']>['firmName'];
    type AccountStage = NonNullable<Account['propFirmDetails']>['accountStage'];
    type AssetClass = NonNullable<Account['propFirmDetails']>['assetClass'];

    // Determine prop firm asset class from selected asset classes
    let assetClass: AssetClass = 'other';
    if (account.selectedAssetClasses && account.selectedAssetClasses.length > 0) {
      const firstAsset = account.selectedAssetClasses[0];
      if (firstAsset === 'futures') {
        assetClass = 'futures';
      } else if (firstAsset === 'forex' || firstAsset === 'cfd') {
        assetClass = 'forex-cfd';
      }
    }

    const propDetails: Account['propFirmDetails'] = {
      assetClass,
      firmName: account.propFirmDetails.firmName as PropFirmName,
      accountStage: account.propFirmDetails.accountStage as AccountStage
    };

    if (account.propFirmDetails.customFirmName) {
      propDetails.customFirmName = account.propFirmDetails.customFirmName;
    }

    entity.propFirmDetails = propDetails;
  }

  return entity;
}

/**
 * Transform opening balance to balance history record
 */
export function transformBalanceHistory(
  draft: OnboardingDraft,
  accountId: string
): BalanceHistory {
  const openingBalance = draft.account?.openingBalance ?? 0;
  const baseCurrency = draft.account?.baseCurrency || 'USD';

  const precision = getPrecisionForCurrency(baseCurrency);
  const balanceInteger = toMoneyInteger(openingBalance, precision);

  return {
    id: generateUUID(),
    createdAt: now(),
    updatedAt: now(),
    accountId,
    timestamp: now(),
    transactionType: 'deposit',
    amount: balanceInteger,
    amountPrecision: precision,
    balanceAfter: balanceInteger,
    balancePrecision: precision,
    description: 'Opening balance',
  };
}

/**
 * Transform account risk configuration from draft to schema
 *
 * Converts risk profiles with tempIds to persistent schema with real IDs,
 * mapping strategyTempId/playbookTempId to actual entity IDs.
 */
export function transformAccountRisk(
  draft: OnboardingDraft,
  accountId: string,
  strategyIdMap: Map<string, string>,
  playbookIdMap: Map<string, string>
): AccountRisk | null {
  const riskConfig = draft.trading?.riskConfiguration;

  if (!riskConfig || !riskConfig.profiles || riskConfig.profiles.length === 0) {
    return null;
  }

  // Transform each profile, replacing tempIds with real IDs
  const riskProfiles: RiskProfile[] = riskConfig.profiles.map((profile, index) => {
    const profileId = generateUUID();

    const transformedProfile: RiskProfile = {
      id: profileId,
      name: profile.name,
      status: 'active' as const,
      order: profile.order ?? index,
      accountDefault: profile.accountDefault
    };

    // Add optional fields
    if (profile.description) {
      transformedProfile.description = profile.description;
    }
    if (profile.color) {
      transformedProfile.color = profile.color;
    }

    // Transform strategy overrides (replace tempIds with real IDs)
    if (profile.strategyOverrides && profile.strategyOverrides.length > 0) {
      transformedProfile.strategyOverrides = profile.strategyOverrides
        .map(override => {
          const realStrategyId = strategyIdMap.get(override.strategyTempId);
          if (!realStrategyId) {
            console.warn(`Strategy tempId ${override.strategyTempId} not found in ID map`);
            return null;
          }
          return {
            strategyId: realStrategyId,
            risk: override.risk
          };
        })
        .filter((o): o is NonNullable<typeof o> => o !== null);
    }

    // Transform playbook overrides (replace tempIds with real IDs)
    if (profile.playbookOverrides && profile.playbookOverrides.length > 0) {
      transformedProfile.playbookOverrides = profile.playbookOverrides
        .map(override => {
          const realPlaybookId = playbookIdMap.get(override.playbookTempId);
          if (!realPlaybookId) {
            console.warn(`Playbook tempId ${override.playbookTempId} not found in ID map`);
            return null;
          }
          return {
            playbookId: realPlaybookId,
            risk: override.risk
          };
        })
        .filter((o): o is NonNullable<typeof o> => o !== null);
    }

    return transformedProfile;
  });

  // Find default profile ID (if specified)
  let defaultProfileId: string | undefined;
  if (riskConfig.defaultProfileTempId) {
    const defaultTempId = riskConfig.defaultProfileTempId;
    const defaultProfileIndex = riskConfig.profiles.findIndex(
      p => p.tempId === defaultTempId
    );
    if (defaultProfileIndex !== -1 && riskProfiles[defaultProfileIndex]) {
      defaultProfileId = riskProfiles[defaultProfileIndex].id;
    }
  }

  const accountRisk: AccountRisk = {
    id: generateUUID(),
    createdAt: now(),
    updatedAt: now(),
    name: 'Risk Configuration',
    description: 'Risk profiles for different market conditions',
    status: 'active' as const,
    accountId,
    riskProfiles,
  };

  if (defaultProfileId) {
    accountRisk.defaultProfileId = defaultProfileId;
  }

  return accountRisk;
}

/**
 * Transform basic settings from draft to schema
 */
export function transformBasicSettings(draft: OnboardingDraft): BasicSettings {
  const personal = draft.personal;

  const entity: BasicSettings = {
    id: generateUUID(),
    createdAt: now(),
    updatedAt: now(),
    preferredLanguage: 'en', // Default to English
    displayTimezone: personal?.displayTimezone || 'America/New_York',
    timeFormat: personal?.timeFormat || '12h',
    weekStartsOn: personal?.weekStartsOn || 'monday',
    tradingSessions: personal?.tradingSessions || [],
    timeframeOptions: [], // Defaults populated via BasicSettingsService
    instrumentCatalog: {
      updateNotifications: true,
      templateSyncPreference: 'prompt'
    },
  };

  return entity;
}

/**
 * Transform all account data in correct dependency order
 */
export function transformAccountData(draft: OnboardingDraft): {
  broker: Broker;
  account: Account;
  balanceHistory: BalanceHistory;
} {
  // Transform broker first (no dependencies)
  const broker = transformBroker(draft);

  // Transform account (depends on broker for fee preferences and default currency)
  const account = transformAccount(draft, broker);

  // Transform balance history (depends on account)
  const balanceHistory = transformBalanceHistory(draft, account.id);

  return {
    broker,
    account,
    balanceHistory
  };
}

/**
 * Transform all onboarding data into persistent schema entities
 *
 * Master composition function that orchestrates all transforms in correct order
 * and returns all entities ready for persistence.
 */
export function transformOnboardingData(draft: OnboardingDraft) {
  // Transform basic settings (no dependencies)
  const basicSettings = transformBasicSettings(draft);

  // Transform trading data (categories, items, playbooks, strategy)
  const tradingData = transformTradingData(draft);

  // Transform account data (broker, account, balance history)
  const accountData = transformAccountData(draft);

  // Transform account risk configuration (depends on account + strategy + playbooks)
  const accountRisk = transformAccountRisk(
    draft,
    accountData.account.id,
    tradingData.strategyIdMap,
    tradingData.playbookIdMap
  );

  return {
    // Basic settings
    basicSettings,
    // Trading entities
    strategy: tradingData.strategy,
    playbooks: tradingData.playbooks,
    entryModels: tradingData.entryModels,
    entryModelCategories: tradingData.entryModelCategories,
    criteriaItems: tradingData.criteriaItems,
    confluences: tradingData.confluences,
    marketConditionCategories: tradingData.marketConditionCategories,

    // Account entities
    broker: accountData.broker,
    account: accountData.account,
    balanceHistory: accountData.balanceHistory,
    accountRisk
  };
}
